import { parseDecimal, type Qty, toQty } from "./quantity.ts";
import type { UnitCode } from "./units.ts";

/** How much one item a shop sells holds: 100 pieces, 1 litre, a pair. */
export interface Pack {
  /** In milli-units, like every quantity (architecture.md › Quantities and money). */
  quantity: Qty;
  unit: UnitCode;
}

/** Unit words in German and English as shops write them, lower case. */
const UNIT_WORDS: Record<string, UnitCode> = {
  stück: "pcs",
  stck: "pcs",
  stk: "pcs",
  st: "pcs",
  pcs: "pcs",
  pc: "pcs",
  piece: "pcs",
  pieces: "pcs",
  paar: "pair",
  pair: "pair",
  pairs: "pair",
  satz: "set",
  set: "set",
  sets: "set",
  l: "l",
  liter: "l",
  litre: "l",
  litres: "l",
  ml: "ml",
  m: "m",
  meter: "m",
  metre: "m",
  cm: "cm",
  kg: "kg",
  g: "g",
  gramm: "g",
  gram: "g",
};

const WORDS = Object.keys(UNIT_WORDS)
  .sort((a, b) => b.length - a.length)
  .join("|");

/** "100 x …" or "2× …" at the start: that many pieces. */
const COUNT = /^\s*(\d{1,5})\s*[x×]\s+\S/i;

/** An amount with a unit as a word of its own: "1 Stück", "1,5 l", "500ml", "1 Paar". */
const AMOUNT = new RegExp(
  `(?:^|[\\s(*_/,;])(\\d{1,6}(?:[.,]\\d{1,3})?)\\s*(${WORDS})\\.?(?=$|[\\s)*_/,;.])`,
  "iu",
);

/**
 * The pack a shop's text describes, or null when it names none: "100 x BMW Bremsklotzpaste" is
 * 100 pieces, "*1 stück*" one piece, "Motoröl 5W-30 1 l" one litre, "1 Paar" a pair. A count at
 * the start wins over an amount later in the text ("100 x Bremsklotzpaste 3g" is 100 pieces). A
 * number glued to letters ("ST4,8X16MM", "W 712/75", "5W-30") is no amount.
 */
export function packOf(text: string): Pack | null {
  const count = COUNT.exec(text);
  if (count?.[1]) {
    const pieces = Number(count[1]);
    return pieces > 0 ? { quantity: toQty(pieces), unit: "pcs" } : null;
  }
  const amount = AMOUNT.exec(text);
  if (!amount?.[1] || !amount[2]) return null;
  const value = parseDecimal(amount[1]);
  const unit = UNIT_WORDS[amount[2].toLowerCase()];
  if (value === null || value <= 0 || !unit) return null;
  return { quantity: toQty(value), unit };
}
